import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useApi from 'react-use-api';
import InfiniteScroll from 'react-infinite-scroll-component';
import ProductLoader from '../../components/Loaders/ProductLoader';
import Product from '../../components/Products/Product';
import ProductCard from '../../components/Modals/ProductCard';
import { EventSourcesData } from '../../utils/DataLayer/constant';
import queries from '../../services/queries';
import { DL_ViewItemList } from '../../utils/DataLayer/DL_ViewItemList';
import { handleData } from '../../utils/handleData';
import { useSelector } from 'react-redux';
import { selectorsFilters } from '../../redux/filters/filtersReducer';
import { IFiltersDTO } from '../../typings/IFilters';
import { selectorsDelivery } from '../../redux/delivery/deliveryReducer';
import { blockBodyScroll } from '../../utils/blockBodyScroll';
import { selectorsOther } from '../../redux/other/otherReducer';
import { useLessThen991 } from '../../utils/mediaQuery';

interface ICategoryProductListProps {
  categoryId: number;
  filters: IFiltersDTO;
  needToResetOffset: boolean;
  tid: string;
  setProductsAreLoading: (value: boolean) => void;
}

export default function ProductList(props: ICategoryProductListProps) {
  const { categoryId, filters, needToResetOffset, tid, setProductsAreLoading } = props;

  const isDesktop = useSelector(selectorsOther.isDesktop);
  const isLessThen991 = useLessThen991(isDesktop);

  const isInCourierArea = useSelector(selectorsDelivery.isInCourierArea);
  const selectedBrands = useSelector(selectorsFilters.getSelectedBrands);
  const selectedBadges = useSelector(selectorsFilters.getSelectedBadges);
  const selectedAllergich = useSelector(selectorsFilters.getSelectedAllergich);
  const selectedNutrion = useSelector(selectorsFilters.getSelectedNutrion);
  const hasPriceRanges = useSelector(selectorsFilters.getHasPriceRanges);
  const hasBadges = useSelector(selectorsFilters.getHasBadges);
  const hasSalePrice = useSelector(selectorsFilters.getHasSalePrice);
  const searchFilter = useSelector(selectorsFilters.getSearchFilter);
  const selectedSort = useSelector(selectorsFilters.getSortDirection);
  const selectedCustomCategory = useSelector(selectorsFilters.getCustomCategory);
  const totalCount = useSelector(selectorsFilters.getTotalSelectedFiltersCount);

  const limit = 20;
  const [offset, setOffset] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedProductData, setSelectedProductData] = useState<{ item: any; index: number }>({
    item: null,
    index: 0,
  });

  const getProducts = (offset?: number, limit?: number) => {
    return queries.getProductsByCategory(
      categoryId,
      filters.orderProducts,
      filters,
      isInCourierArea,
      limit,
      offset,
      tid,
    );
  };

  const options = useMemo(
    () => ({
      handleData,
      dependencies: {
        perPage: limit,
      },
      skip: isNaN(categoryId),
    }),
    [categoryId, limit],
  );
  const [products, { loading, hasMore = true }, request] = useApi(getProducts(), options);
  const loadMore = useCallback(() => {
    const newOffset = offset + limit;
    request(getProducts(newOffset, limit), true).then(() => setOffset(newOffset));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    offset,
    selectedBrands,
    selectedBadges,
    selectedAllergich,
    selectedNutrion,
    hasPriceRanges,
    hasBadges,
    hasSalePrice,
    searchFilter,
    selectedSort,
    selectedCustomCategory,
    categoryId,
  ]);

  useEffect(() => {
    if (products?.length) {
      const currentDlProducts = offset ? [...products].slice(offset) : products;
      DL_ViewItemList(currentDlProducts, EventSourcesData.CATEGORY, offset);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  useEffect(() => {
    setProductsAreLoading(loading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    if (!isOpen) {
      blockBodyScroll(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isLessThen991) {
      setOffset(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount, categoryId]);

  useEffect(() => {
    setOffset(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSort]);

  useEffect(() => {
    if (needToResetOffset) {
      setOffset(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needToResetOffset]);

  const openProductCardPopup = (product: any, index: number) => {
    if (!isOpen) {
      setIsOpen(true);
      setSelectedProductData({ item: product, index });
    }
  };

  const closeProductCardPopup = () => {
    if (isOpen) {
      setIsOpen(false);
      setSelectedProductData({ item: null, index: 0 });
    }
  };

  return (
    <>
      <InfiniteScroll
        next={loadMore}
        hasMore={hasMore && !loading}
        loader={<ProductLoader />}
        dataLength={products?.length || 0}
        scrollThreshold={0.8}
        style={{ overflowX: 'hidden' }}>
        {!!products?.length &&
          products.map((product: any, index: number) => (
            <div key={product.id} className="col-6 col-md-4 col-lg-3 col-xl-2 catalog-item">
              <Product
                product={product}
                onProductImageClick={(product: any) => openProductCardPopup(product, index)}
                dlEcommerceEventSource={EventSourcesData.CATEGORY}
                dlItemIndex={index}
              />
            </div>
          ))}
      </InfiniteScroll>
      {isOpen && !!selectedProductData?.item?.id && (
        <ProductCard
          isOpen={isOpen}
          id={selectedProductData.item.id}
          dlItemIndex={selectedProductData.index}
          onProductCardClose={closeProductCardPopup}
        />
      )}
    </>
  );
}
